import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'

import { useAdminMutation, useAdminQuery } from '../api'
import { I18nField, SelectField, TextField, type I18nValue } from './fields'
import type { CashRegister } from '../types'

type Station = 'none' | 'bar' | 'kitchen' | 'door'

/**
 * Les caisses de la soirée.
 *
 * Un poste de vente n'est pas une session : la caisse est le meuble — le bar,
 * l'entrée, le comptoir —, la session est le service qu'un caissier y ouvre et
 * ferme en comptant son fonds. Les sessions se tiennent sur `/ops/pos` ; les
 * caisses, elles, se créent ici, une fois pour toutes.
 *
 * Le **poste** rattaché n'est pas décoratif : une caisse « bar » ne propose
 * — et n'accepte — que des boissons, ce qui évite qu'on encaisse un plat au
 * comptoir des boissons et que la cuisine ne le voie jamais.
 *
 * Une caisse se **modifie** et se **désactive** ; elle ne s'efface que si
 * personne ne l'a jamais tenue. Le serveur refuse le reste : effacer une
 * caisse effacerait les rapports des services qui y ont été tenus —
 * précisément ce qu'on relit quand un écart est contesté. Jusqu'ici l'écran
 * ne savait que créer : une faute de frappe dans un code restait pour toute
 * la soirée, et une caisse retirée continuait d'être proposée aux caissiers.
 */
export function RegistersPanel({ eventId }: { eventId: string | undefined }) {
  const { t, i18n } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [armed, setArmed] = useState<string | null>(null)

  const query = useAdminQuery<CashRegister[]>(
    ['admin-registers', eventId],
    `/admin/events/${eventId}/finance/registers`,
    eventId,
  )
  const refresh = [['admin-registers', eventId ?? ''], ['pos-registers', eventId ?? '']]

  const create = useAdminMutation<CashRegister, Record<string, unknown>>(
    'post',
    () => `/admin/events/${eventId}/finance/registers`,
    eventId,
    refresh,
  )
  const update = useAdminMutation<CashRegister, Record<string, unknown> & { id: string }>(
    'patch',
    ({ id }) => `/admin/events/${eventId}/finance/registers/${id}`,
    eventId,
    refresh,
  )
  const remove = useAdminMutation<unknown, { id: string }>(
    'delete',
    ({ id }) => `/admin/events/${eventId}/finance/registers/${id}`,
    eventId,
    refresh,
  )

  if (query.isPending) return <Skeleton className="h-40 w-full" />
  // Sans le droit de lecture, le panneau s'efface plutôt que d'afficher une
  // erreur : ce n'est pas une panne, c'est un écran qui ne le concerne pas.
  if (query.isError) return null

  const rows = query.data ?? []
  const label = (value: Record<string, string>) =>
    value[i18n.language] || Object.values(value)[0] || ''
  const rowError = update.error?.message ?? remove.error?.message ?? null

  return (
    <Card className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-heading-md text-content-primary">
            {t('admin.registers.title')}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-content-secondary">
            {t('admin.registers.intro')}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setAdding(!adding)}>
          {adding ? t('admin.project.cancel') : t('admin.registers.add')}
        </Button>
      </div>

      {rows.length === 0 && !adding ? (
        <p className="mt-5 text-sm text-content-secondary">{t('admin.registers.empty')}</p>
      ) : null}

      {rowError ? <p className="mt-4 text-sm text-state-danger">{rowError}</p> : null}

      {rows.length > 0 ? (
        <ul className="mt-5 grid gap-2">
          {rows.map((row) => (
            <li key={row.id} className="rounded-lg border border-line-subtle px-4 py-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm text-content-primary">
                    {label(row.name_i18n) || row.code}
                  </span>
                  <span className="block text-caption text-content-muted">
                    <span className="font-mono">{row.code}</span>
                    {row.location ? ` · ${row.location}` : ''}
                  </span>
                </span>
                {row.station && row.station !== 'none' ? (
                  <Badge tone="neutral">{t(`admin.registers.station.${row.station}`)}</Badge>
                ) : null}
                {!row.is_active ? (
                  <Badge tone="ember">{t('admin.registers.inactive')}</Badge>
                ) : null}
              </div>

              <div className="mt-2 flex flex-wrap gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(editing === row.id ? null : row.id)}
                >
                  {editing === row.id ? t('admin.project.cancel') : t('admin.registers.edit')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ id: row.id, is_active: !row.is_active })}
                >
                  {row.is_active ? t('admin.registers.deactivate') : t('admin.registers.activate')}
                </Button>
                {armed === row.id ? (
                  <Button
                    size="sm"
                    variant="ember"
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate({ id: row.id }, { onSettled: () => setArmed(null) })
                    }
                  >
                    {t('admin.registers.confirmDelete')}
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => setArmed(row.id)}>
                    {t('admin.registers.delete')}
                  </Button>
                )}
              </div>

              {editing === row.id ? (
                <RegisterForm
                  initial={row}
                  submitLabel={t('common.save')}
                  busy={update.isPending}
                  error={null}
                  onSubmit={(payload) =>
                    update.mutate(
                      { id: row.id, ...payload },
                      { onSuccess: () => setEditing(null) },
                    )
                  }
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {adding ? (
        <RegisterForm
          submitLabel={t('admin.registers.create')}
          busy={create.isPending}
          error={create.error ? create.error.message : null}
          onSubmit={(payload) =>
            create.mutate({ ...payload, is_active: true }, { onSuccess: () => setAdding(false) })
          }
        />
      ) : null}
    </Card>
  )
}

function RegisterForm({
  initial,
  submitLabel,
  busy,
  error,
  onSubmit,
}: {
  initial?: CashRegister
  submitLabel: string
  busy: boolean
  error: string | null
  onSubmit: (payload: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  const [code, setCode] = useState(initial?.code ?? '')
  const [name, setName] = useState<I18nValue>(initial?.name_i18n ?? {})
  const [location, setLocation] = useState(initial?.location ?? '')
  const [station, setStation] = useState<Station>(initial?.station ?? 'none')

  return (
    <div className="mt-4 grid gap-4 border-t border-line-subtle pt-4">
      <TextField
        label={t('admin.registers.code')}
        hint={t('admin.registers.codeHint')}
        value={code}
        onChange={setCode}
      />
      <I18nField label={t('admin.registers.name')} value={name} onChange={setName} />
      <TextField
        label={t('admin.registers.location')}
        hint={t('admin.registers.locationHint')}
        value={location}
        onChange={setLocation}
      />
      <SelectField
        label={t('admin.registers.stationLabel')}
        hint={t('admin.registers.stationHint')}
        value={station}
        onChange={(value) => setStation(value as Station)}
        options={[
          { value: 'none', label: t('admin.registers.station.none') },
          { value: 'bar', label: t('admin.registers.station.bar') },
          { value: 'kitchen', label: t('admin.registers.station.kitchen') },
          { value: 'door', label: t('admin.registers.station.door') },
        ]}
      />

      {error ? <p className="text-sm text-state-danger">{error}</p> : null}

      <div>
        <Button
          size="sm"
          disabled={busy || !code.trim()}
          onClick={() =>
            onSubmit({
              code: code.trim(),
              name_i18n: name,
              location: location.trim() || null,
              station,
            })
          }
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  )
}
